import { useCallback, useEffect, useMemo, useState } from 'react';

import { UserProfileShortPopoverTrigger } from '@modules/user/UserProfileShortPopoverTrigger';
import { Button, Stack, TextField, Typography, styled } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import { Doc } from 'src/@types/docs';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { useAuthContext } from 'src/auth/useAuthContext';
import { PopoverTrigger } from 'src/components/PopoverTrigger';
import { Iconify } from 'src/components/iconify';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { useWorkspaceUser } from 'src/hooks/useWorkspace';
import { useLocales } from 'src/locales';
import { UpdateDocPayload } from 'src/requests/docs';
import { FormatTime } from 'src/utils/formatTime';
import { CoverPickerPopOver } from '../shared/CoverPickerPopOver';
import { IconPickerPopOver } from '../shared/IconPickerPopOver';
import { UNTITLED } from '../utils';
import { CONTAINER_PADDING } from './constants';

const TitleInput = styled(TextField)`
  .MuiInputBase-root {
    color: var(--emphasis-high);
    font-size: 30px;
    font-weight: bold;
    font-family: 'Nunito', 'helvetica', sans-serif, 'noto color emoji';

    ::placeholder {
      color: var(--emphasis-low);
    }

    .MuiOutlinedInput-input {
      padding: 0;
    }

    .MuiOutlinedInput-notchedOutline {
      border: none;
    }
  }
`;
const ButtonStyled = styled(Button)(({ theme }) => ({
  color: theme.palette.text.disabled,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: 'transparent',
  '&:hover': {
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.text.primary}`,
  },
}));

const EllipsisText = styled('span')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
});

const CoverIconContainer = styled(Button)(({ theme }) => ({
  fontSize: theme.spacing(5),
  padding: 0,
  lineHeight: 1,
  minWidth: 'unset',
  width: theme.spacing(7),
  height: theme.spacing(6),
}));

type Props = {
  doc: Doc;
  updateDoc(_doc: UpdateDocPayload): Promise<Doc>;
};

export const DocTitle = ({ doc, updateDoc }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const docOwner = useWorkspaceUser(doc.owner);
  const { user } = useAuthContext();
  const [title, setTitle] = useState(doc?.title || '');
  const { translate } = useLocales();
  const { isSmallMobile } = useBreakpoints();

  const updateTitle = useCallback(async () => {
    if (!doc || title === doc.title) return;

    await updateDoc({
      _id: doc._id,
      title: title.trim() || UNTITLED,
    });
  }, [doc, title, updateDoc]);

  useEffect(() => {
    if (doc) setTitle(doc.title);
  }, [doc]);

  const iconsTitle = useMemo(() => {
    const base = `collaborate.docs.docs_title`;
    if (isSmallMobile) {
      return {
        addCover: `${base}.add_cover_mobile`,
        addIcon: `${base}.add_icon_mobile`,
        comments: `${base}.comments`,
      };
    }
    return {
      addCover: `${base}.add_cover`,
      addIcon: `${base}.add_icon`,
      comments: `${base}.comments`,
    };
  }, [isSmallMobile]);

  return (
    <>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        px={{ xs: 0, sm: CONTAINER_PADDING }}
        sx={{ pr: { xs: 0, sm: CONTAINER_PADDING } }}
      >
        {!doc?.cover && (
          <PopoverTrigger popover={CoverPickerPopOver} context={{ doc }}>
            <ButtonStyled
              variant="soft"
              color="inherit"
              size="small"
              className="no-print"
              startIcon={<Iconify icon="tabler:photo-plus" />}
            >
              <EllipsisText>{translate(iconsTitle.addCover)}</EllipsisText>
            </ButtonStyled>
          </PopoverTrigger>
        )}
        {!doc?.icon && (
          <PopoverTrigger popover={IconPickerPopOver} context={{ doc }}>
            <ButtonStyled
              variant="soft"
              color="inherit"
              size="small"
              className="no-print"
              startIcon={<Iconify icon="tabler:mood-plus" />}
            >
              <EllipsisText>{translate(iconsTitle.addIcon)}</EllipsisText>
            </ButtonStyled>
          </PopoverTrigger>
        )}
        <ButtonStyled
          variant="soft"
          color="inherit"
          size="small"
          startIcon={<Iconify icon="tabler:message-dots" />}
          onClick={() =>
            navigate(PATH_DASHBOARD.docs.comments(doc._id), {
              state: { background: location },
            })
          }
          className="no-print"
        >
          <EllipsisText>{translate(iconsTitle.comments)}</EllipsisText>
        </ButtonStyled>
      </Stack>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        mt={1}
        px={{ sm: CONTAINER_PADDING }}
        sx={{ pr: { xs: 0, sm: CONTAINER_PADDING } }}
      >
        {doc.icon && (
          <PopoverTrigger popover={IconPickerPopOver} context={{ doc }}>
            <CoverIconContainer>
              <em-emoji shortcodes={doc.icon?.value} />
            </CoverIconContainer>
          </PopoverTrigger>
        )}
        <TitleInput
          fullWidth
          variant="outlined"
          value={title === UNTITLED ? '' : title}
          placeholder={UNTITLED}
          onChange={(e: any) => setTitle(e.target.value)}
          onBlur={updateTitle}
        />
      </Stack>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 0, sm: 0.5 }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mt={1}
        mb={2}
        sx={{ color: 'text.secondary' }}
        px={{ sm: CONTAINER_PADDING }}
      >
        {docOwner && docOwner?._id === user?._id ? (
          <Typography variant="caption">
            {translate('collaborate.docs.docs_title.created_by_you', {
              createdAt: FormatTime.Date(doc.createdAt),
            })}
          </Typography>
        ) : (
          <Typography variant="caption">
            {translate('shared.create_by')}{' '}
            <UserProfileShortPopoverTrigger userId={docOwner?._id}>
              <span>{docOwner?.name}</span>
            </UserProfileShortPopoverTrigger>{' '}
            {translate('shared.on_date', {
              createdAt: FormatTime.Date(doc.createdAt),
            })}
          </Typography>
        )}
        <Iconify
          icon="tabler:point-filled"
          sx={{ width: 10 }}
          display={{ xs: 'none', sm: 'block' }}
        />
        <Typography variant="caption">
          {translate('shared.last_edited_date', {
            updatedAt: FormatTime.Date(doc.updatedAt),
          })}
        </Typography>
      </Stack>
    </>
  );
};
